<template>
  <v-row no-gutters>
    <v-col cols="4" class="justify-center ma-auto pa-1">
      <div class="text-h5 font-weight-bold ma-0 pa-1">
        <span data-test-name="region-name">{{ name }}</span>
        <span v-if="flags.suspended" class="ml-2" style="background-color: rgba(255, 255, 255, 0.5); padding: 3px; border: 1px solid black; border-radius: 8px">SUSP</span>
        <span v-if="ap" class="ml-2" style="background-color: rgba(255, 255, 255, 0.5); padding: 3px; border: 1px solid black; border-radius: 8px">AP</span>
      </div>
    </v-col>

    <v-col class="ma-0 pa-1">
      <v-sheet :color="energyBackground" class="justify-center rounded ma-auto pa-1">
        <v-tooltip location="bottom center" origin="auto">
          <template #activator="{ props }">
            <span v-bind="props" class="text-h5 font-weight-bold ma-auto">
              <a :href="trendLink" target="_blank" data-test-name="energy-price" :data-test-region-name="name">{{ energyPrice }}</a>
              <v-icon v-if="changeIcon" class="ml-1 mr-1 mb-1" :icon="changeIcon" />
              <v-icon v-else class="ml-1 mr-1 mb-1" icon="mdi-unfold-less-vertical" color="transparent" style="width: 0" />
            </span>
            <span v-if="administered" class="text-caption font-weight-bold mb-1" style="background-color: rgba(255, 255, 255, 0.5); padding: 3px; border: 1px solid black; border-radius: 8px">AP</span>
            <span v-if="vollOverride" class="text-caption font-weight-bold mb-1 ml-1" style="background-color: rgba(255, 255, 255, 0.5); padding: 3px; border: 1px solid black; border-radius: 8px"
              >VO</span
            >
            <span v-if="manualOverride" class="text-caption font-weight-bold mb-1 ml-1" style="background-color: rgba(255, 255, 255, 0.5); padding: 3px; border: 1px solid black; border-radius: 8px"
              >MO</span
            >
            <span v-if="mpc" class="text-caption font-weight-bold mb-1 ml-1" style="background-color: rgba(255, 255, 255, 0.5); padding: 3px; border: 1px solid black; border-radius: 8px">MPC</span>
            <span v-if="mpf" class="text-caption font-weight-bold mb-1 ml-1" style="background-color: rgba(255, 255, 255, 0.5); padding: 3px; border: 1px solid black; border-radius: 8px">MPF</span>
            <span v-if="irlfScaling" class="text-caption font-weight-bold mb-1 ml-1" style="background-color: rgba(255, 255, 255, 0.5); padding: 3px; border: 1px solid black; border-radius: 8px"
              >IRLF</span
            >
          </template>
          <span class="font-weight-bold">{{ name }} Energy price</span><br />
          <span v-if="flags.suspended">Market suspended (SUSP)<br /></span>
          <span v-if="administered">Administered price (AP)<br /></span>
          <span v-if="vollOverride">VoLL override (VO)<br /></span>
          <span v-if="manualOverride">Price manually overwritten (MO)<br /></span>
          <span v-if="mpc">Market price cap binding (MPC)<br /></span>
          <span v-if="mpf">Market price floor binding (MPF)<br /></span>
          <span v-if="irlfScaling">Inter-regional price scaling applied (IRLF)<br /></span>
          <span>Current: {{ energyPrice }}</span
          ><br />
          <span>Previous: {{ previousEnergyPrice }}</span
          ><br />
          <span>Change: {{ deltaPrice }} ({{ percentChange.toFixed(2) }}%)</span><br />
        </v-tooltip>
      </v-sheet>
    </v-col>

    <!-- <v-col sm="1">
        <v-container fluid grid-list-sm pa-1>
            <v-row row wrap justify-end>
                <v-col v-for="alert in alerts" :key="alert.name" xs6 text-caption>
                    <v-layout justify-end fill-height rounded ma-0 pa-0>
                        <v-sheet
                            :color="alert.color"
                            class="pa-1 font-weight-bold"
                            style="font-size: 9px;"
                        >{{ alert.name }}</v-sheet>
                    </v-layout>
                </v-col>
            </v-row>
        </v-container>
    </v-col>-->
  </v-row>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import HeatMap from '@//heatMaps/heatMap';
import changeLogic from '@/components/changeLogic';
import { mapState } from 'pinia';
import { useStore } from '@/stores/main';
import { RegionPrices } from '@/repositories';
import { stringifyQuery } from 'vue-router';

export default defineComponent({
  props: {
    name: { type: String, default: '' },
    price: { type: Number, default: 0 },
    previousPrice: { type: Number, default: 0 },
    flags: { type: RegionPrices, default: new RegionPrices() },
    ap: Boolean,
    alerts: { type: Array, default: () => [] }
  },
  computed: {
    ...mapState(useStore, ['priceMode']),
    energyPrice() {
      return `$${this.price?.toFixed(2)}`;
    },
    previousEnergyPrice() {
      return `$${this.previousPrice?.toFixed(2)}`;
    },
    deltaPrice() {
      return `$${changeLogic.change(this.price, this.previousPrice).toFixed(2)}`;
    },
    percentChange() {
      return changeLogic.percentChange(this.price, this.previousPrice);
    },
    changeIcon() {
      return changeLogic.changeIcon(this.price, this.previousPrice);
    },
    trendLink() {
      return `https://trends.global-roam.com/#/artifacts/F103D4E5-B351-4BD2-A615-DF40333D5F4E-${this.name}1-energy?${stringifyQuery(this.$route.query)}`;
    },
    energyBackground() {
      return HeatMap.default.getColor(this.price ?? 0, 0, 326).toRgba(0.75);
    },
    administered() {
      return this.flags?.energyFlags?.includes('Administered') && this.priceMode === 'financial';
    },
    vollOverride() {
      return this.flags?.energyFlags?.includes('VollOverride');
    },
    manualOverride() {
      return this.flags?.energyFlags?.includes('ManualOverride');
    },
    mpc() {
      return this.flags?.energyFlags?.includes('MPC');
    },
    mpf() {
      return this.flags?.energyFlags?.includes('MPF');
    },
    irlfScaling() {
      return this.flags?.energyFlags?.includes('IRLFScaling');
    }
  }
});
</script>

<style scoped>
a {
  color: black;
  text-decoration: none;
}

.text-h5 {
  font-size: 12pt !important;
}
</style>
